// Backstage-free view-model for search: the hit shape, facet types and the
// display-label helpers. Facet counts come from the catalog facets API and
// results are filtered + paginated server-side by the container, so this module
// holds only shared types and pure label helpers.

export interface SearchHit {
  /** Result type from the backend, e.g. 'software-catalog' | 'techdocs'. */
  type: string;
  title: string;
  text: string;
  /** In-app path to navigate to on click (client-side, no reload). */
  location: string;
  kind?: string;
  /** All owners of the entity (full refs); empty for non-catalog results. */
  owners?: string[];
  lifecycle?: string;
}

/**
 * Selected facet values. Every facet is a list so the same shape backs both the
 * multi-select facets (Type, Owner) and the single-select ones (Kind,
 * Lifecycle, which hold at most one value). An empty list means "all".
 */
export interface SelectedFacets {
  type: string[];
  kind: string[];
  owner: string[];
  lifecycle: string[];
}

export type FacetKey = keyof SelectedFacets;

export const EMPTY_FACETS: SelectedFacets = {
  type: [],
  kind: [],
  owner: [],
  lifecycle: [],
};

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export interface FacetOptions {
  type: FacetOption[];
  kind: FacetOption[];
  owner: FacetOption[];
  lifecycle: FacetOption[];
}

const RESULT_TYPE_KEYS: Record<string, string> = {
  'software-catalog': 'search.resultType.softwareCatalog',
  techdocs: 'search.resultType.techdocs',
};

/**
 * i18n message key for a well-known result type's display label, if any.
 * Unknown types (future search collators) have no chrome label — callers
 * fall back to rendering the raw `type` string.
 */
export function resultTypeKey(type: string): string | undefined {
  return RESULT_TYPE_KEYS[type];
}

/**
 * Humanize an owner entity ref for display, dropping the `kind:namespace/`
 * prefix: `group:default/platform-team` → `platform-team`. The full ref is kept
 * as the facet/filter value; only the label changes.
 */
export function ownerLabel(owner: string): string {
  const slash = owner.lastIndexOf('/');
  if (slash >= 0) return owner.slice(slash + 1);
  const colon = owner.indexOf(':');
  return colon >= 0 ? owner.slice(colon + 1) : owner;
}
